// Public entry point for the DDL structure-extraction surface. Pure function
// — produces a `DdlStructure` from a `ParsedBuffer` + a statement reference.
// Structural-edit forms: CREATE TABLE / ALTER TABLE / CREATE INDEX / DROP
// INDEX. Seed-only forms (VDE-2 — grammar-backed replacements for the removed
// regex parked parsers; the arm carries the parsed seed, not a structural
// edit): GRANT / REVOKE, CREATE TYPE / CREATE DOMAIN, CREATE/ALTER/DROP
// TRIGGER, CREATE/ALTER/DROP FUNCTION|PROCEDURE. Any other DDL head throws so
// unintended callers fail loudly rather than silently.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      CREATE TABLE.

import type { ParseOptions, StatementInfo } from '../../sql-grammar/model';
import type { DdlStructure, ParsedBuffer } from '../types';

import { skipWsCommentsLocal, tryConsumeKeyword } from './extract/shared';
import { extractCreateTable } from './extract/createTable';
import { extractAlterTable } from './extract/alterTable';
import { extractCreateIndex } from './extract/createIndex';
import { extractDropIndex } from './extract/dropIndex';
import { extractGrant } from './extract/grant';
import { extractType } from './extract/type';
import { extractTrigger } from './extract/trigger';
import { extractRoutine } from './extract/routine';

/** Classify the DDL statement by its leading keyword(s) (CREATE/ALTER/DROP
 *  TABLE/INDEX; GRANT/REVOKE; CREATE TYPE/DOMAIN; CREATE/ALTER/DROP TRIGGER;
 *  CREATE/ALTER/DROP FUNCTION|PROCEDURE) so the right extractor runs. Pure
 *  surface scan — does not require the grammar to have classified the
 *  statement beyond `kind` (which the resolver's coarseKind already does). */
type DdlHead =
  | 'create-table'
  | 'alter-table'
  | 'create-index'
  | 'drop-index'
  | 'create-type'
  | 'create-domain'
  | 'create-trigger'
  | 'alter-trigger'
  | 'drop-trigger'
  | 'create-routine'
  | 'alter-routine'
  | 'drop-routine'
  | 'grant'
  | 'revoke'
  | 'unknown';

const CREATE_TABLE_MODIFIERS: ReadonlySet<string> = new Set([
  'TEMP', 'TEMPORARY', 'GLOBAL', 'LOCAL',
  'UNLOGGED', 'VOLATILE', 'PRIVATE',
  'SHARDED', 'DUPLICATED',
]);

const CREATE_INDEX_MODIFIERS: ReadonlySet<string> = new Set([
  'UNIQUE', 'CLUSTERED', 'NONCLUSTERED', 'BITMAP', 'FULLTEXT', 'SPATIAL',
]);

function classifyDdlHead(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): DdlHead {
  let i = skipWsCommentsLocal(source, start, end, options);
  const afterCreate = tryConsumeKeyword(source, i, end, options, 'CREATE');
  if (afterCreate !== i) {
    i = skipWsCommentsLocal(source, afterCreate, end, options);
    i = consumeAnyOf(source, options, i, end, CREATE_TABLE_MODIFIERS);
    i = consumeAnyOf(source, options, i, end, CREATE_INDEX_MODIFIERS);
    i = skipWsCommentsLocal(source, i, end, options);
    if (tryConsumeKeyword(source, i, end, options, 'TABLE') !== i) return 'create-table';
    if (tryConsumeKeyword(source, i, end, options, 'INDEX') !== i) return 'create-index';
    // Type / domain forms (VDE-2 Slice 2). `CREATE [OR REPLACE]
    // [IF [NOT] EXISTS] TYPE|DOMAIN`. Checked AFTER TABLE/INDEX so a plain
    // `CREATE TABLE` / `CREATE VIEW` is never misclassified — `OR REPLACE`
    // and `IF NOT EXISTS` are stepped past with the same primitive idiom the
    // INDEX-modifier consumption uses, no regex.
    let t = i;
    const afterOr = tryConsumeKeyword(source, t, end, options, 'OR');
    if (afterOr !== t) {
      const o = skipWsCommentsLocal(source, afterOr, end, options);
      const afterReplace = tryConsumeKeyword(source, o, end, options, 'REPLACE');
      if (afterReplace !== o) t = skipWsCommentsLocal(source, afterReplace, end, options);
    }
    const afterIf = tryConsumeKeyword(source, t, end, options, 'IF');
    if (afterIf !== t) {
      let j = skipWsCommentsLocal(source, afterIf, end, options);
      const afterNot = tryConsumeKeyword(source, j, end, options, 'NOT');
      if (afterNot !== j) j = skipWsCommentsLocal(source, afterNot, end, options);
      const afterExists = tryConsumeKeyword(source, j, end, options, 'EXISTS');
      if (afterExists !== j) t = skipWsCommentsLocal(source, afterExists, end, options);
    }
    if (tryConsumeKeyword(source, t, end, options, 'TYPE') !== t) return 'create-type';
    if (tryConsumeKeyword(source, t, end, options, 'DOMAIN') !== t) return 'create-domain';
    // Trigger forms (VDE-2 Slice 3). `CREATE [OR REPLACE | OR ALTER]
    // [CONSTRAINT] [EDITIONABLE|NONEDITIONABLE] TRIGGER`. Checked AFTER
    // TABLE/INDEX/TYPE/DOMAIN so those are never misclassified; TEMP/TEMPORARY
    // was already consumed by CREATE_TABLE_MODIFIERS above. Modifiers are
    // stepped past with the same `tryConsumeKeyword`/`skipWsCommentsLocal`
    // idiom, no regex. `OR` disambiguates REPLACE vs ALTER (mssql).
    let g = i;
    const gAfterOr = tryConsumeKeyword(source, g, end, options, 'OR');
    if (gAfterOr !== g) {
      const o = skipWsCommentsLocal(source, gAfterOr, end, options);
      const gAfterReplace = tryConsumeKeyword(source, o, end, options, 'REPLACE');
      if (gAfterReplace !== o) g = skipWsCommentsLocal(source, gAfterReplace, end, options);
      else {
        const gAfterAlter = tryConsumeKeyword(source, o, end, options, 'ALTER');
        if (gAfterAlter !== o) g = skipWsCommentsLocal(source, gAfterAlter, end, options);
      }
    }
    const gAfterConstraint = tryConsumeKeyword(source, g, end, options, 'CONSTRAINT');
    if (gAfterConstraint !== g) g = skipWsCommentsLocal(source, gAfterConstraint, end, options);
    const gAfterEd = tryConsumeKeyword(source, g, end, options, 'EDITIONABLE');
    if (gAfterEd !== g) g = skipWsCommentsLocal(source, gAfterEd, end, options);
    else {
      const gAfterNed = tryConsumeKeyword(source, g, end, options, 'NONEDITIONABLE');
      if (gAfterNed !== g) g = skipWsCommentsLocal(source, gAfterNed, end, options);
    }
    if (tryConsumeKeyword(source, g, end, options, 'TRIGGER') !== g) return 'create-trigger';
    // Routine forms (VDE-2 Slice 4). `CREATE [OR REPLACE | OR ALTER]
    // [EDITIONABLE|NONEDITIONABLE] [DEFINER = …] {FUNCTION|PROCEDURE|PROC}`.
    // Checked AFTER TABLE/INDEX/TYPE/DOMAIN/TRIGGER so none of those is ever
    // misclassified. Modifiers are stepped past with the same
    // `tryConsumeKeyword`/`skipWsCommentsLocal` idiom, no regex. `OR`
    // disambiguates REPLACE vs ALTER (mssql); the mysql `DEFINER = <value>`
    // clause is skipped through the `=` + a value run.
    let r = i;
    const rAfterOr = tryConsumeKeyword(source, r, end, options, 'OR');
    if (rAfterOr !== r) {
      const o = skipWsCommentsLocal(source, rAfterOr, end, options);
      const rAfterReplace = tryConsumeKeyword(source, o, end, options, 'REPLACE');
      if (rAfterReplace !== o) r = skipWsCommentsLocal(source, rAfterReplace, end, options);
      else {
        const rAfterAlter = tryConsumeKeyword(source, o, end, options, 'ALTER');
        if (rAfterAlter !== o) r = skipWsCommentsLocal(source, rAfterAlter, end, options);
      }
    }
    const rAfterEd = tryConsumeKeyword(source, r, end, options, 'EDITIONABLE');
    if (rAfterEd !== r) r = skipWsCommentsLocal(source, rAfterEd, end, options);
    else {
      const rAfterNed = tryConsumeKeyword(source, r, end, options, 'NONEDITIONABLE');
      if (rAfterNed !== r) r = skipWsCommentsLocal(source, rAfterNed, end, options);
    }
    const rAfterDefiner = tryConsumeKeyword(source, r, end, options, 'DEFINER');
    if (rAfterDefiner !== r) {
      let d = skipWsCommentsLocal(source, rAfterDefiner, end, options);
      if (source[d] === '=') {
        d = skipWsCommentsLocal(source, d + 1, end, options);
        while (d < end && source[d] !== ' ' && source[d] !== '\t' && source[d] !== '\n'
          && source[d] !== '\r' && source[d] !== '\f' && source[d] !== '\v'
          && source[d] !== ',') d++;
        r = skipWsCommentsLocal(source, d, end, options);
      }
    }
    if (tryConsumeKeyword(source, r, end, options, 'PROCEDURE') !== r) return 'create-routine';
    if (tryConsumeKeyword(source, r, end, options, 'PROC') !== r) return 'create-routine';
    if (tryConsumeKeyword(source, r, end, options, 'FUNCTION') !== r) return 'create-routine';
    return 'unknown';
  }
  const afterAlter = tryConsumeKeyword(source, i, end, options, 'ALTER');
  if (afterAlter !== i) {
    const j = skipWsCommentsLocal(source, afterAlter, end, options);
    if (tryConsumeKeyword(source, j, end, options, 'TABLE') !== j) return 'alter-table';
    if (tryConsumeKeyword(source, j, end, options, 'TRIGGER') !== j) return 'alter-trigger';
    if (tryConsumeKeyword(source, j, end, options, 'PROCEDURE') !== j) return 'alter-routine';
    if (tryConsumeKeyword(source, j, end, options, 'PROC') !== j) return 'alter-routine';
    if (tryConsumeKeyword(source, j, end, options, 'FUNCTION') !== j) return 'alter-routine';
    return 'unknown';
  }
  const afterDrop = tryConsumeKeyword(source, i, end, options, 'DROP');
  if (afterDrop !== i) {
    const j = skipWsCommentsLocal(source, afterDrop, end, options);
    if (tryConsumeKeyword(source, j, end, options, 'INDEX') !== j) return 'drop-index';
    if (tryConsumeKeyword(source, j, end, options, 'TRIGGER') !== j) return 'drop-trigger';
    if (tryConsumeKeyword(source, j, end, options, 'PROCEDURE') !== j) return 'drop-routine';
    if (tryConsumeKeyword(source, j, end, options, 'PROC') !== j) return 'drop-routine';
    if (tryConsumeKeyword(source, j, end, options, 'FUNCTION') !== j) return 'drop-routine';
    return 'unknown';
  }
  if (tryConsumeKeyword(source, i, end, options, 'GRANT') !== i) return 'grant';
  if (tryConsumeKeyword(source, i, end, options, 'REVOKE') !== i) return 'revoke';
  return 'unknown';
}

/** Try every keyword in `targets` at `i`; on match, advance past the keyword
 *  and continue trying (chained modifiers like CREATE UNIQUE CLUSTERED INDEX
 *  consume two modifiers before INDEX). */
function consumeAnyOf(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
  targets: ReadonlySet<string>,
): number {
  let i = start;
  // Bound the loop so a degenerate input cannot run forever.
  for (let iter = 0; iter < 8; iter++) {
    const ws = skipWsCommentsLocal(source, i, end, options);
    let advanced = ws;
    for (const t of targets) {
      const next = tryConsumeKeyword(source, ws, end, options, t);
      if (next !== ws) { advanced = next; break; }
    }
    if (advanced === ws) return i;
    i = advanced;
  }
  return i;
}

export function extractDdlStructure(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const { source, options } = buffer.parsedDocument;
  const head = classifyDdlHead(
    source, options,
    statementInfo.range.startOffset,
    statementInfo.range.endOffset,
  );

  if (head === 'create-table') return extractCreateTable(buffer, statementInfo);
  if (head === 'alter-table') return extractAlterTable(buffer, statementInfo);
  if (head === 'create-index') return extractCreateIndex(buffer, statementInfo);
  if (head === 'drop-index') return extractDropIndex(buffer, statementInfo);
  if (head === 'create-type' || head === 'create-domain') return extractType(buffer, statementInfo);
  if (head === 'create-trigger' || head === 'alter-trigger' || head === 'drop-trigger') {
    return extractTrigger(buffer, statementInfo);
  }
  if (head === 'create-routine' || head === 'alter-routine' || head === 'drop-routine') {
    return extractRoutine(buffer, statementInfo);
  }
  if (head === 'grant' || head === 'revoke') return extractGrant(buffer, statementInfo);

  throw new Error(
    `extractDdlStructure: '${head}' is not a recognised DDL form `
      + `(supported: CREATE TABLE / ALTER TABLE / CREATE INDEX / DROP INDEX; `
      + `GRANT / REVOKE; CREATE TYPE / CREATE DOMAIN; CREATE/ALTER/DROP `
      + `TRIGGER; CREATE/ALTER/DROP FUNCTION|PROCEDURE)`,
  );
}
